import { DownOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import { AppConfirmation } from '@nxsys/components/AppConfirmationModal';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppSelectSync from '@nxsys/components/AppSelectSync';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import {
  PAYMENT_MODEL_ENUM,
  getIrelandPaymentModels,
} from '@nxsys/constants/AppConstants';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import {
  useApprovePayroll,
  useRejectPayroll,
  useRequestApprover,
} from '@nxsys/hooks/api/dashboard';
import {
  useConfirmMultipleDraftPayroll,
  useDeleteDraftPayrollById,
  useDownloadAllDraftPayroll,
  useGetDraftPayroll,
  useReversalMultipleDraftPayroll,
  useSendEmailDraftPayslipMultiple,
  useViewDraftPayslipById,
} from '@nxsys/hooks/api/payroll';
import useNotificationSocket from '@nxsys/hooks/useNotificationSocket';
import usePayrollSocket from '@nxsys/hooks/usePayrollSocket';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { reportActions } from '@nxsys/store/slices/report/reducer/reportSlice';
import { GetDraftPayrollListParams } from '@nxsys/store/slices/report/types';
import { Icons } from '@nxsys/types/icons';
import { DraftPayroll } from '@nxsys/types/models/payroll';
import DraftPayrollActionCell from '../../reports/payroll/listing/DraftPayrollActionCell';
import { isEmailDraftAllowed } from '../../reports/payroll/listing/draftPayslipEmail';
import { getTaxWeekTaxYear } from '@nxsys/utils';
import { csvConfigExport } from '@nxsys/utils/export';
import { buildDraftPayrollExport } from '../../reports/payroll/listing/draftPayrollExport';
import { showMessage } from '@nxsys/utils/message';
import { trackPayslipZip } from '@nxsys/utils/payslip-zip-download';
import { Dropdown, Tag } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { ExportToCsv } from 'export-to-csv';
import { debounce } from 'lodash';
import moment from 'moment-timezone';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import DownloadAllModal, {
  DownloadAllValues,
} from '../../reports/payroll/components/DownloadAllModal';
import {
  getDraftPayrollBureauColumns,
  getDraftPayrollPayeUmbrellaColumns,
} from '../../reports/payroll/listing/columns';
import RequestApprovalModal, {
  RequestApprovalValues,
} from './RequestApprovalModal';
import { verifierColumn } from '../../reports/payroll/listing/verifierColumn';
import PayslipVerdictModal from '../../reports/payroll/listing/PayslipVerdictModal';
import { useVerifyPayslip } from '@nxsys/hooks/api/payroll';

export enum DraftPayrollModalTypeEnum {
  REQUEST_APPROVAL = 'REQUEST_APPROVAL',
  CONFIRM_PAYROLL = 'CONFIRM_PAYROLL',
  AWAITING_APPROVAL = 'AWAITING_APPROVAL',
  CLIENT_REJECTION = 'CLIENT_REJECTION',
}

type Props = {
  isOpen: boolean;
  toggle: () => void;
  type: DraftPayrollModalTypeEnum;
  cbAfterAction?: () => void;
};

const DraftPayrollModal: React.FC<Props> = ({
  isOpen,
  toggle,
  type,
  cbAfterAction = () => {},
}) => {
  const { messages } = useTypedIntl();
  const { payrollCountry, user, isBureau } = useSelector(
    (state: AppState) => state.appStore,
  );
  const currentTaxYear = getTaxWeekTaxYear().taxYear;
  const isIreland = payrollCountry === IRELAND;
  const validPaymentModels = isIreland
    ? getIrelandPaymentModels(isBureau)
    : [PAYMENT_MODEL_ENUM.PAYEE];

  const dispatch = useDispatch();
  const { draftPayrollList } = useSelector(
    (state: AppState) => state.reportStore,
  );
  const { list, pagination, loading, queryParams } = draftPayrollList;

  // NX-4971 — rows whose verification is being (re-)run: show a RUNNING spinner
  // immediately (optimistic) until the verify socket fires and we refetch.
  const [rerunningIds, setRerunningIds] = useState<string[]>([]);
  const displayList = useMemo(
    () =>
      (list ?? []).map((row: DraftPayroll) =>
        rerunningIds.includes(row.id)
          ? {
              ...row,
              payslipVerificationRef: {
                ...row.payslipVerificationRef,
                status: 'RUNNING' as const,
                verdict: null,
              },
            }
          : row,
      ),
    [list, rerunningIds],
  );

  const [selectedRows, setSelectedRows] = useState<DraftPayroll[]>([]);

  const [selectedPayrollId, setSelectedPayrollId] = useState<string | null>(
    null,
  );
  const [isOpenRequestApprovalModal, setIsOpenRequestApprovalModal] =
    useState(false);
  // NX-4971 — advisory payslip verifier (view verdict detail / re-run).
  const [verdictRecord, setVerdictRecord] = useState<DraftPayroll | null>(null);
  const { run: verifyPayslip, loading: isVerifyLoading } =
    useVerifyPayslip();
  const handleViewVerdict = (record: DraftPayroll) => setVerdictRecord(record);
  const handleRerunVerification = (record: DraftPayroll) => {
    setVerdictRecord(null); // close the verdict modal immediately on re-run
    setRerunningIds((prev) =>
      prev.includes(record.id) ? prev : [...prev, record.id],
    );
    verifyPayslip({ draftPayrollIds: [record.id] })
      .then((res) => {
        if (res?.success) {
          showMessage({
            type: 'info',
            content:
              'Re-verification requested. The verdict will update shortly.',
          });
        } else {
          setRerunningIds((prev) => prev.filter((id) => id !== record.id));
        }
      })
      .catch(() => {
        setRerunningIds((prev) => prev.filter((id) => id !== record.id));
      });
  };

  const renderTitle = useCallback(() => {
    let title = '';
    if (type === DraftPayrollModalTypeEnum.CONFIRM_PAYROLL) {
      title = 'Confirm Payroll';
    } else if (type === DraftPayrollModalTypeEnum.REQUEST_APPROVAL) {
      title = 'Request Approval';
    } else if (type === DraftPayrollModalTypeEnum.AWAITING_APPROVAL) {
      title = 'Awaiting Approval';
    } else if (type === DraftPayrollModalTypeEnum.CLIENT_REJECTION) {
      title = 'Client Rejection';
    }
    return (
      <AppPageTitle
        prefix={title}
        separator=""
        title=""
        className="px-[10px]"
      />
    );
  }, [type]);

  const renderRightContent = () => {
    return (
      <div className="flex items-center gap-2 mr-[48px]">
        {type === DraftPayrollModalTypeEnum.CONFIRM_PAYROLL && (
          <AppButton
            name="Confirm"
            onClick={handleConfirmSelectedDraftPayroll}
          />
        )}
        <Dropdown
          menu={{
            items: [
              ...(type === DraftPayrollModalTypeEnum.AWAITING_APPROVAL
                ? [
                    {
                      key: 'approve',
                      label: 'Approve',
                      onClick: handleApprovePayrollSelectedDraftPayroll,
                    },
                    {
                      key: 'reject',
                      label: 'Reject',
                      onClick: handleRejectPayrollSelectedDraftPayroll,
                    },
                  ]
                : []),
              {
                key: 'delete',
                label: 'Delete',
                onClick: handleReversalSelectedDraftPayroll,
              },
              {
                key: 'download',
                label: 'Download All',
                onClick: () => setIsOpenDownloadAllModal(true),
              },
              {
                key: 'exportAll',
                label: 'Export All',
                onClick: handleExportAll,
              },
              {
                key: 'exportSelected',
                label: 'Export Selected',
                onClick: handleExportSelected,
              },
              ...(type === DraftPayrollModalTypeEnum.CONFIRM_PAYROLL
                ? [
                    {
                      key: 'sendEmailDraftPayslip',
                      label: messages['common.sendEmailDraftPayslip'],
                      // Needs at least one company in the list that allows it.
                      disabled: !(list ?? []).some(isEmailDraftAllowed),
                      onClick: handleSendDraftPayslipSelected,
                    },
                  ]
                : []),
            ],
          }}
        >
          <AppButton name="Actions" rightIcon={<DownOutlined />} />
        </Dropdown>
      </div>
    );
  };

  useEffect(() => {
    if (isOpen)
      handleFetchData({
        page: 0,
        size: 20,
      });
  }, [isOpen]);

  const handleFetchData = (params: GetDraftPayrollListParams) => {
    const newParams = {
      ...params,
      paymentModels: validPaymentModels,
    };

    if (type === DraftPayrollModalTypeEnum.CONFIRM_PAYROLL) {
      newParams.payrollApproved = true;
    } else if (type === DraftPayrollModalTypeEnum.REQUEST_APPROVAL) {
      newParams.payrollApproveNeeded = 'TRUE';
      newParams.isRequestedUserApprovePayroll = false;
      newParams.payrollApproved = 'null';
    } else if (type === DraftPayrollModalTypeEnum.AWAITING_APPROVAL) {
      newParams.payrollApproveNeeded = 'TRUE';
      newParams.isRequestedUserApprovePayroll = true;
      newParams.payrollApproved = 'null';
    } else if (type === DraftPayrollModalTypeEnum.CLIENT_REJECTION) {
      newParams.payrollApproved = false;
    }

    dispatch(reportActions.getDraftPayrollList(newParams));
  };

  const refetchData = () => {
    handleFetchData({
      ...queryParams,
    });
    cbAfterAction();
  };

  // NX-4971 — verification runs in the background (auto on draft-gen, or on re-run).
  // Refresh the list when the verify job fires so the Verifier column advances from
  // its spinner to the verdict, and clear the optimistic RUNNING markers.
  usePayrollSocket({
    eventName: `payslip-verify-${user?.id}`,
    onSuccess: () => {
      setRerunningIds([]);
      refetchData();
    },
  });

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleSearch = useCallback(
    debounce(
      (e: React.ChangeEvent<HTMLInputElement>) =>
        handleFetchData({ ...queryParams, search: e.target.value }),
      500,
    ),
    [queryParams],
  );

  const handleFilter = (params: Partial<GetDraftPayrollListParams>) => {
    handleFetchData({
      ...queryParams,
      ...params,
    });
  };

  const { run: viewDraftPayslipById, loading: isViewDraftPayslipByIdLoading } =
    useViewDraftPayslipById();
  const handleViewDraftPayslipById = (id: string) => {
    viewDraftPayslipById(id).then((res) => {
      if (res.success) {
        window.open(res.data, '_blank');
      } else {
        showMessage({
          type: 'error',
          content: messages['common.somethingWentWrong'],
        });
      }
    });
  };

  const {
    run: confirmMultipleDraftPayroll,
    loading: isConfirmMultipleDraftPayrollLoading,
  } = useConfirmMultipleDraftPayroll();
  const handleConfirmMultipleDraftPayroll = (ids: string[]) => {
    AppConfirmation.open({
      title: messages['common.confirmDraftPayroll'],
      paragraph: messages['common.areYouSureYouWantToConfirm'],
      type: 'warning',
      icon: 'IconWarning',
      okText: messages['common.confirm'],
      onConfirm: () => {
        confirmMultipleDraftPayroll(ids).then((res) => {
          if (res.success) {
            showMessage({
              type: 'success',
              content: messages['common.draftPayrollConfirmed'],
            });
            refetchData();
          }
        });
      },
    });
  };
  const handleConfirmDraftPayroll = (id: string) => {
    handleConfirmMultipleDraftPayroll([id]);
  };
  const handleConfirmSelectedDraftPayroll = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleConfirmMultipleDraftPayroll(selectedRows.map((item) => item.id));
  };

  const {
    run: deleteDraftPayrollById,
    loading: isDeleteDraftPayrollByIdLoading,
  } = useDeleteDraftPayrollById();
  const handleDeleteDraftPayroll = (id: string) => {
    AppConfirmation.open({
      title: messages['common.deleteDraftPayroll'],
      paragraph: messages['common.areYouSureYouWantToDelete'],
      type: 'warning',
      icon: 'IconWarning',
      okText: messages['common.delete'],
      onConfirm: () => {
        deleteDraftPayrollById(id).then((res) => {
          if (res.success) {
            showMessage({
              type: 'success',
              content: messages['common.draftPayrollDeleted'],
            });
            refetchData();
          }
        });
      },
    });
  };

  const handleOpenRequestApproval = (id: string) => {
    setSelectedPayrollId(id);
    setIsOpenRequestApprovalModal(true);
  };

  const { run: approvePayroll, loading: approvePayrollLoading } =
    useApprovePayroll();
  const handleApprovePayroll = async (ids: string[]) => {
    const res = await approvePayroll({ ids });
    if (res.success) {
      showMessage({
        type: 'success',
        content: 'Payroll approved successfully',
      });
      refetchData();
    }
  };
  const handleApprovePayrollSelectedDraftPayroll = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleApprovePayroll(selectedRows.map((item) => item.id));
  };

  const { run: rejectPayroll, loading: rejectPayrollLoading } =
    useRejectPayroll();
  const handleRejectPayroll = async (ids: string[]) => {
    const res = await rejectPayroll({ ids });
    if (res.success) {
      showMessage({
        type: 'success',
        content: 'Payroll rejected successfully',
      });
      refetchData();
    }
  };
  const handleRejectPayrollSelectedDraftPayroll = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleRejectPayroll(selectedRows.map((item) => item.id));
  };

  const {
    run: reversalMultipleDraftPayroll,
    loading: isReversalMultipleDraftPayrollLoading,
  } = useReversalMultipleDraftPayroll();
  const handleReversalMultipleDraftPayroll = (data: DraftPayroll[]) => {
    AppConfirmation.open({
      title: messages['common.deleteDraftPayroll'],
      paragraph: messages['common.areYouSureYouWantToDelete'],
      type: 'warning',
      icon: 'IconWarning',
      okText: messages['common.delete'],
      onConfirm: () => {
        const ids = data.map((item) => item.id);
        reversalMultipleDraftPayroll({
          draftPayrollIds: ids,
          isDraft: true,
          payrollIds: [],
        }).then(() => {
          showMessage({
            type: 'info',
            content: messages['common.draftPayrollDeleteSuccessPending'],
          });
        });
      },
    });
  };
  const handleReversalSelectedDraftPayroll = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleReversalMultipleDraftPayroll(selectedRows);
  };
  useNotificationSocket({
    eventName: `reversal-draft-payroll-${user?.id}`,
    onSuccess: () => {
      showMessage({
        type: 'success',
        content: messages['common.draftPayrollReversalSuccess'],
      });
      refetchData();
    },
    onError: () => {
      showMessage({
        type: 'error',
        content: messages['common.draftPayrollReversalFailed'],
      });
    },
  });

  // NX-6289: email draft payslips before the bank file.
  const {
    run: sendEmailDraftPayslipMultiple,
    loading: isSendEmailDraftPayslipMultipleLoading,
  } = useSendEmailDraftPayslipMultiple();
  const handleSendDraftPayslipMultiple = (data: DraftPayroll[]) => {
    // Only the rows whose company allows emailing draft payslips are sent.
    const allowed = data.filter(isEmailDraftAllowed);
    if (!allowed.length) {
      showMessage({
        type: 'warning',
        content: messages['common.emailDraftPayslipDisabled'],
      });
      return;
    }
    sendEmailDraftPayslipMultiple(allowed.map((item) => item.id)).then((res) => {
      if (res.success) {
        showMessage({
          type: 'success',
          content: messages['common.emailSentDraftPayslipPending'],
        });
      }
    });
  };
  const handleSendDraftPayslip = (record: DraftPayroll) => {
    handleSendDraftPayslipMultiple([record]);
  };
  const handleSendDraftPayslipSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleSendDraftPayslipMultiple(selectedRows);
  };
  // The modal stays mounted after closing; only the open Confirm Payroll
  // modal reacts, so ConfirmPayroll's own listener doesn't double the toast.
  const isEmailDraftModalOpen =
    isOpen && type === DraftPayrollModalTypeEnum.CONFIRM_PAYROLL;
  useNotificationSocket({
    eventName: `send-mail-draft-self-bill-${user?.id}`,
    onSuccess: () => {
      if (!isEmailDraftModalOpen) return;
      showMessage({
        type: 'success',
        content: messages['common.emailSentDraftPayslipSuccess'],
      });
      refetchData();
    },
    onError: () => {
      if (!isEmailDraftModalOpen) return;
      showMessage({
        type: 'error',
        content: messages['common.emailSentDraftPayslipFailed'],
      });
    },
  });

  const [isOpenDownloadAllModal, setIsOpenDownloadAllModal] = useState(false);
  const {
    run: downloadAllDraftPayroll,
    loading: isDownloadAllDraftPayrollLoading,
  } = useDownloadAllDraftPayroll();
  const handleDownloadAll = async (values: DownloadAllValues) => {
    const res = await downloadAllDraftPayroll({
      companyPaymentId: values.companyPayment?.value || '',
      agencyId: values.agency?.value || '',
      taxYear: values.taxYear,
      taxPeriodFrom: values.taxPeriodFrom,
      taxPeriodTo: values.taxPeriodTo,
      paymentModels: validPaymentModels,
    });
    if (res.success) {
      trackPayslipZip(res.data.jobId, res.data.total);
    }
  };

  const getActionByType = () => {
    switch (type) {
      case DraftPayrollModalTypeEnum.CONFIRM_PAYROLL: {
        return [
          {
            key: 'actions',
            title: messages['common.action'],
            align: 'left' as const,
            render: (record: DraftPayroll) => {
              return (
                <DraftPayrollActionCell
                  record={record}
                  onDownload={handleViewDraftPayslipById}
                  onConfirm={handleConfirmDraftPayroll}
                  onDelete={handleDeleteDraftPayroll}
                  onSendEmail={handleSendDraftPayslip}
                />
              );
            },
            width: 150,
            fixed: 'left',
          },
        ];
      }
      case DraftPayrollModalTypeEnum.REQUEST_APPROVAL: {
        return [
          {
            key: 'actions',
            title: messages['common.action'],
            align: 'left' as const,
            render: (record: DraftPayroll) => {
              return (
                <ul className="flex justify-start items-center">
                  <li onClick={() => handleOpenRequestApproval(record.id)}>
                    <Icons.IconUserGroup width={16} height={16} />
                  </li>
                  {record?.isPAL && (
                    <li>
                      <Tag color="warning" className="ml-1">
                        PAL
                      </Tag>
                    </li>
                  )}
                </ul>
              );
            },
            width: 100,
            fixed: 'left',
          },
        ];
      }
      case DraftPayrollModalTypeEnum.AWAITING_APPROVAL: {
        return [
          {
            key: 'actions',
            title: messages['common.action'],
            align: 'left' as const,
            render: (record: DraftPayroll) => {
              return (
                <ul className="flex justify-start items-center gap-x-[8px]">
                  <li onClick={() => handleApprovePayroll([record.id])}>
                    <Icons.IconCheckCircle width={16} height={16} />
                  </li>
                  <li onClick={() => handleRejectPayroll([record.id])}>
                    <Icons.IconCloseCircle width={16} height={16} />
                  </li>
                  {record?.isPAL && (
                    <li>
                      <Tag color="warning" className="ml-1">
                        PAL
                      </Tag>
                    </li>
                  )}
                </ul>
              );
            },
            width: 100,
            fixed: 'left',
          },
        ];
      }
      default: {
        return [];
      }
    }
  };

  // This modal shows draft values, so its data columns are the Draft Payroll
  // report columns verbatim. Only the action cell differs (per modal type), so
  // the report's own action column is dropped.
  const reportColumns: ColumnsType<DraftPayroll> = isBureau
    ? getDraftPayrollBureauColumns({
        messages,
        payrollCountry,
        handleViewDraftPayslipById,
        handleConfirmDraftPayroll,
        handleDeleteDraftPayroll,
      })
    : getDraftPayrollPayeUmbrellaColumns({
        messages,
        payrollCountry,
        handleViewDraftPayslipById,
        handleConfirmDraftPayroll,
        handleDeleteDraftPayroll,
        isBureau,
      });

  const columns: ColumnsType<DraftPayroll> = [
    ...((getActionByType() as any) ?? []),
    // Verifier is dashboard-only (super admin) and stays right after the
    // action cell.
    ...(user?.isSuperAdmin
      ? [verifierColumn(messages, handleViewVerdict)]
      : []),
    ...reportColumns.filter((column) => column.key !== 'actions'),
  ];

  const { run: requestApprover, loading: requestApproverLoading } =
    useRequestApprover();
  const handleRequestApprover = async (values: RequestApprovalValues) => {
    const res = await requestApprover(selectedPayrollId ?? '', {
      ids: values.approverIds?.map((item) => item.value ?? '') ?? [],
    });
    if (res.success) {
      showMessage({
        type: 'success',
        content: 'Request approval successfully',
      });
      refetchData();
    }
  };

  const handleExport = (rows: DraftPayroll[]) => {
    // Headers/values come from `columns` so the export can never drift from
    // what the table shows.
    const { headers, dataExport } = buildDraftPayrollExport({
      data: rows,
      columns,
      payrollCountry,
    });

    const exportName = csvConfigExport(
      `Payroll-export-${moment(new Date()).tz('Europe/London').format('DD/MM/YYYY')}`,
      headers,
    );
    const csvExporter = new ExportToCsv(exportName);
    csvExporter.generateCsv(dataExport);
  };
  const handleExportSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleExport(selectedRows);
  };
  const { run: getDraftPayroll, loading: isGetDraftPayrollLoading } =
    useGetDraftPayroll();
  const handleExportAll = async () => {
    if (!pagination.totalDocs) {
      showMessage({
        type: 'warning',
        content: messages['common.noDataToExport'],
      });
      return;
    }
    // queryParams carries the table's page/size; Export All must not stop at
    // the current page.
    const res = await getDraftPayroll({
      ...queryParams,
      page: 0,
      size: pagination.totalDocs,
    });
    if (res.success) {
      handleExport(res.data);
    }
  };

  return (
    <AppModalContainer
      title={null}
      isOpen={isOpen}
      toggle={toggle}
      className="max-sm:w-[100vw"
      width={1250}
      minHeight="0"
      noPaddingBody
      hiddenFooter
    >
      <StyledContentContainer>
        <AppsContainer
          fullView
          type="bottom"
          title={renderTitle()}
          rightContent={renderRightContent()}
          loading={
            isViewDraftPayslipByIdLoading ||
            isConfirmMultipleDraftPayrollLoading ||
            isDeleteDraftPayrollByIdLoading ||
            requestApproverLoading ||
            approvePayrollLoading ||
            rejectPayrollLoading ||
            isGetDraftPayrollLoading ||
            isReversalMultipleDraftPayrollLoading ||
            isDownloadAllDraftPayrollLoading ||
            isSendEmailDraftPayslipMultipleLoading
          }
        >
          <div className="flex justify-between w-full gap-4 flex-wrap mb-[20px] px-[10px]">
            <div className="flex flex-wrap gap-4 flex-1">
              <AppSelectSync
                type="company"
                apiType="searchCompany"
                placeholder="Select Payment Company"
                mode="multiple"
                onChangeValue={(value: any) => {
                  handleFilter({
                    companyRefs:
                      value?.map((item: any) => item.value) ?? undefined,
                  });
                }}
                paramFilter={{ paymentModels: validPaymentModels }}
                className="!w-[260px]"
              />
            </div>
            <AppInputSearch onChange={handleSearch} />
          </div>
          <AppsContent>
            <AppTableContainer
              rowKey="id"
              data={displayList}
              loading={loading}
              page={pagination.current}
              total={pagination.totalDocs}
              pageSize={pagination.pageSize}
              scroll={{ x: 'max-content', y: 700 }}
              columns={columns}
              onChangePagination={onChangePagination}
              rowSelection={{
                type: 'checkbox',
                onChange: (_: React.Key[], selectedRows: any[]) => {
                  setSelectedRows(selectedRows);
                },
              }}
            />
          </AppsContent>
        </AppsContainer>
      </StyledContentContainer>
      <RequestApprovalModal
        isOpen={isOpenRequestApprovalModal}
        toggle={() => setIsOpenRequestApprovalModal(false)}
        onSubmit={handleRequestApprover}
        payrollId={selectedPayrollId ?? ''}
      />
      <PayslipVerdictModal
        isOpen={!!verdictRecord}
        toggle={() => setVerdictRecord(null)}
        record={verdictRecord}
        onRerun={handleRerunVerification}
        rerunLoading={isVerifyLoading}
        onSaved={refetchData}
      />
      <DownloadAllModal
        isOpen={isOpenDownloadAllModal}
        toggle={() => setIsOpenDownloadAllModal(!isOpenDownloadAllModal)}
        onDownload={handleDownloadAll}
        initialValues={{
          taxYear: queryParams?.taxYear ?? currentTaxYear,
        }}
      />
    </AppModalContainer>
  );
};

const StyledContentContainer = styled.div`
  padding-top: 10px;

  > div > div:nth-child(2) {
    background-color: ${defaultTheme.theme.palette.white};
    min-height: 200px;
    height: fit-content;

    > div > div > div {
      background-color: ${defaultTheme.theme.palette.white};
    }
  }
`;

export default DraftPayrollModal;
